import React, { useState } from 'react';
import ReorderItems from '../reorder-items.jsx';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../shadcn-ui/select.jsx';
import { SelectValue } from '@radix-ui/react-select';
import { Button } from '../shadcn-ui/button.jsx';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { insertOrUpdateLotProcessStep } from '@/network/api.service.js';
import { useNavigate } from 'react-router-dom';

const SortableLotProcess = ({ id }) => {
  const [loading, setLoading] = useState(false);
  const { control, handleSubmit } = useFormContext();
  const navigate = useNavigate();

  const { fields, replace, append, remove } = useFieldArray({
    control,
    name: 'processing_steps',
  });

  const watchedSteps = useWatch({ control, name: 'processing_steps' });

  const handleReorder = newItems => {
    const itemsWithUpdatedSequence = newItems.map((item, index) => ({
      ...item,
      sequence: index + 1,
    }));

    replace(itemsWithUpdatedSequence);
  };

  const handleRemove = idx => {
    remove(idx);
    // Update sequences after removal
    const updatedSteps = watchedSteps.filter((_, index) => index !== idx);
    const itemsWithUpdatedSequence = updatedSteps.map((item, index) => ({
      ...item,
      sequence: index + 1,
    }));
    replace(itemsWithUpdatedSequence);
  };

  const handleAddStep = () => {
    append({
      sequence: fields.length + 1,
      name: '',
      status: 'Yet to start',
    });
  };

  const onSubmit = async data => {
    const prodcessStepsChars =
      watchedSteps?.reduce((acc, step) => acc + step?.name?.charAt(0)?.toUpperCase(), '') ?? '';

    if (!data.processing_steps || data.processing_steps.length === 0) data.processing_steps = null;

    try {
      setLoading(true);

      const { data: respData } = await insertOrUpdateLotProcessStep(
        { ...data, process_steps_chars: prodcessStepsChars },
        id
      );

      if (respData?.success) {
        toast.success('Lot process has been saved.');
        navigate(`/process/lot/${id}/step`, { replace: true });
      }
    } catch (error) {
      toast.error('Failed to save lot process');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <ReorderItems
        items={watchedSteps ?? []}
        // items={fields}
        onReorder={handleReorder}
        renderItem={(item, idx) => {
          const currentName = watchedSteps?.[idx]?.name ?? item?.name;

          return (
            <div key={idx} className="flex w-full items-center justify-between">
              <div className="flex items-center text-sm font-medium">
                {/* shows instantly from watch */}
                {currentName ? (
                  <>
                    <div className="mr-1">{item.sequence}.</div>
                    <div>{watchedSteps?.[idx]?.name}</div>
                  </>
                ) : (
                  <Controller
                    name={`processing_steps.${idx}.name`}
                    control={control}
                    rules={{ required: 'Processing step is required' }}
                    render={({ field, fieldState }) => (
                      <div>
                        <Select
                          // ensure the Select value is undefined when empty
                          value={field.value}
                          onValueChange={val => {
                            console.log(val);

                            // ensure stored value is exact allowed string
                            field.onChange(val);
                          }}
                        >
                          <SelectTrigger className="w-[150px] rounded-lg bg-white text-sm">
                            <SelectValue placeholder="Select step" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Pulping">Pulping</SelectItem>
                            <SelectItem value="Washing">Washing</SelectItem>
                            <SelectItem value="Fermentation">Fermentation</SelectItem>
                          </SelectContent>
                        </Select>
                        {fieldState.error && <p className="mt-1 text-xs text-red-600">{fieldState.error.message}</p>}
                      </div>
                    )}
                  />
                )}
              </div>

              <button type="button" onClick={() => handleRemove(idx)} className="h-4 w-4 flex-shrink-0 text-red-500">
                <Trash2 className="h-full w-full" />
              </button>
            </div>
          );
        }}
      />
      <div className="flex flex-col gap-2">
        <Button variant="outline" className="rounded-[10px] border-dashed bg-white" onClick={handleAddStep}>
          <Plus className="mr-2" /> Add Steps
        </Button>
        <Button onClick={handleSubmit(onSubmit)} disabled={loading} className="rounded-[10px]">
          Save Progress
        </Button>
      </div>
    </div>
  );
};

export default SortableLotProcess;
